/**
 * Browser-side cohort preview for the uploader wizard: mirrors
 * UploaderEnrollmentCohortAttributes + DxCodeDeviceMapper (see app/services) and
 * Postgres MBI checks in whiteglove-cli/src/core/views.py.
 *
 * Keep column order aligned with ExportPatientEnrollmentHelper::HEADERS (eligibility Patient CSV).
 */

/** @typedef {{ prefix: string, device: string, chronic_care: boolean }} DxMapRow */

export const UPLOADER_PREVIEW_CSV_HEADERS = [
  "pid",
  "first_name",
  "last_name",
  "dx_codes",
  "signature_method",
  "devices",
  "glucose_measurements_per_day",
  "primary_phone_type",
  "primary_phone",
  "phone_2_type",
  "phone_2_number",
  "phone_3_type",
  "phone_3_number",
  "email",
  "dob",
  "gender",
  "street",
  "city",
  "state",
  "zip_code",
  "primary_insurance_number",
  "primary_insurance_name",
  "primary_insurance_group_num",
  "secondary_insurance_type",
  "secondary_insurance_provider_name",
  "secondary_insurance_group_number",
  "secondary_insurance_individual_number",
  "secondary_insurance_notes",
  "secondary_insurance_policyholder_is_patient",
  "secondary_insurance_patient_relationship_to_policyholder",
  "secondary_insurance_first_name",
  "secondary_insurance_last_name",
  "secondary_insurance_policyholder_dob",
  "secondary_insurance_policyholder_sex",
  "tertiary_insurance_type",
  "tertiary_insurance_provider_name",
  "tertiary_insurance_group_number",
  "tertiary_insurance_individual_number",
  "tertiary_insurance_notes",
  "tertiary_insurance_policyholder_is_patient",
  "tertiary_insurance_patient_relationship_to_policyholder",
  "tertiary_insurance_first_name",
  "tertiary_insurance_last_name",
  "tertiary_insurance_policyholder_dob",
  "tertiary_insurance_policyholder_sex",
  "provider_email",
  "age",
  "is_active_mbi",
  "is_legacy_mbi",
  "is_dx_match",
  "is_med_adv",
  "is_no_insurance_match",
  "is_sixty_five_plus",
  "dx_requires_approval",
  "missing_demographics",
  "errors",
  "enrollable_medicare",
  "verifiable_medadv",
  "no_dx_or_insurance",
  "has_dx_no_insurance",
  "no_dx_has_insurance",
  "has_prev_dx_codes",
  "has_latest_dx_codes",
  "qualified_dx_codes",
  "patient_ehr_id",
  "language"
]

const CODE_PATTERN = /\b[A-TV-Z]\d{2}\.\d{1,4}\b|\b[A-TV-Z]\d{2,7}\b/g

const LEGACY_MBI_RE = /^(?!(000|666|9))\d{3}(?!00)\d{2}(?!0000)\d{4}[a-zA-Z]$/i

const ACTIVE_MBI_RE =
  /^[1-9][AC-HJKMNP-RT-Yac-hjkmnp-rt-y][AC-HJKMNP-RT-Yac-hjkmnp-rt-y0-9][0-9]-?[AC-HJKMNP-RT-Yac-hjkmnp-rt-y][AC-HJKMNP-RT-Yac-hjkmnp-rt-y0-9][0-9]-?[AC-HJKMNP-RT-Yac-hjkmnp-rt-y]{2}\d{2}$/i

const MEDADV_MATCHERS = ["medicare", "advantage", "plus", "choice", "dual"]

const COHORT_BOOL_HEADERS = new Set([
  "is_active_mbi",
  "is_legacy_mbi",
  "is_dx_match",
  "is_med_adv",
  "is_no_insurance_match",
  "is_sixty_five_plus",
  "dx_requires_approval",
  "enrollable_medicare",
  "verifiable_medadv",
  "no_dx_or_insurance",
  "has_dx_no_insurance",
  "no_dx_has_insurance",
  "has_prev_dx_codes",
  "has_latest_dx_codes"
])

function splitPlain(piece) {
  return piece.split(/[,;|]/).map((s) => s.trim()).filter(Boolean)
}

function normalizeTokenList(tokens) {
  const out = []
  const seen = new Set()
  for (let t of tokens) {
    let s = String(t).trim()
    s = s.replace(/^["']|["']$/g, "")
    s = s.replace(/^\[+/, "").replace(/\]+$/, "").trim()
    if (!s || seen.has(s)) continue
    seen.add(s)
    out.push(s)
  }
  return out
}

function extractCodes(s) {
  const m = String(s).match(CODE_PATTERN)
  return m ? normalizeTokenList(m) : []
}

/**
 * Uppercase, dedupe, no ASCII periods — matches {DxCodesNormalizer.coerce_export_string_without_dots}.
 * @param {unknown} value
 */
export function exportDxCodesStringWithoutDots(value) {
  const list = coerceDxCodes(value)
  const seen = new Set()
  const parts = []
  for (const c of list) {
    const n = String(c).toUpperCase().replace(/\./g, "")
    if (!n || seen.has(n)) continue
    seen.add(n)
    parts.push(n)
  }
  return parts.join(", ")
}

export function coerceDxCodes(value) {
  if (value == null) return []
  const parts = Array.isArray(value)
    ? value.map((v) => String(v).trim()).filter(Boolean)
    : [String(value).trim()].filter(Boolean)
  if (!parts.length) return []
  const blob = parts.join(", ")
  const extracted = extractCodes(blob)
  if (extracted.length) return normalizeTokenList(extracted)
  return normalizeTokenList(parts.flatMap((p) => splitPlain(p)))
}

/** @param {string[]} dxCodes @param {DxMapRow[]} mappingRows */
export function dxDeviceMapForCodes(dxCodes, mappingRows) {
  const codes = dxCodes.map((c) => String(c).trim()).filter(Boolean)
  if (!codes.length || !mappingRows.length) {
    return { devices: [], qualifiedDxCodes: [], dxRequiresApproval: false }
  }
  const matchedRows = []
  const qualifiedTokens = []
  for (const dx of codes) {
    const norm = dx.toUpperCase().replace(/\./g, "")
    if (!norm) continue
    const hits = mappingRows.filter((r) => norm.startsWith(String(r.prefix || "")))
    if (!hits.length) continue
    qualifiedTokens.push(dx)
    matchedRows.push(...hits)
  }
  const devices = [...new Set(matchedRows.map((r) => String(r.device || "").trim()).filter(Boolean))]
  const qualifiedDxCodes = [...new Set(qualifiedTokens)]
  const dxRequiresApproval = matchedRows.length > 0 && !matchedRows.some((r) => r.chronic_care === true)
  return { devices, qualifiedDxCodes, dxRequiresApproval }
}

function legacyMbi(insNorm) {
  return Boolean(insNorm && LEGACY_MBI_RE.test(insNorm))
}

function activeMbi(insNorm) {
  return Boolean(insNorm && ACTIVE_MBI_RE.test(insNorm))
}

/** @param {string | null | undefined} s */
function parseIsoDob(s) {
  if (s == null) return null
  const t = String(s).trim()
  const m = t.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return Number.isNaN(d.getTime()) ? null : d
}

/** @param {Date | null} d */
function ageInYears(d) {
  if (!d) return null
  const now = new Date()
  let years = now.getFullYear() - d.getFullYear()
  const md = now.getMonth() - d.getMonth()
  if (md < 0 || (md === 0 && now.getDate() < d.getDate())) years -= 1
  return years
}

function strAttr(attrs, k) {
  const v = attrs[k]
  if (v == null) return ""
  return String(v).trim()
}

function signatureMethodBlank(value) {
  if (value == null) return true
  if (Array.isArray(value)) return value.map((x) => String(x).trim()).filter(Boolean).length === 0
  return String(value).trim() === ""
}

/** @param {Record<string, unknown>} attrs */
function buildMissingDemographics(attrs) {
  const missing = []
  if (!strAttr(attrs, "dob")) missing.push("dob")
  if (!strAttr(attrs, "first_name")) missing.push("first_name")
  if (!strAttr(attrs, "last_name")) missing.push("last_name")
  if (signatureMethodBlank(attrs.signature_method)) missing.push("signature_method")
  if (!strAttr(attrs, "primary_phone")) missing.push("primary_phone")
  if (!strAttr(attrs, "primary_phone_type")) missing.push("primary_phone_type")
  if (!strAttr(attrs, "email")) missing.push("email")
  if (!strAttr(attrs, "gender")) missing.push("gender")
  if (!strAttr(attrs, "street")) missing.push("street")
  if (!strAttr(attrs, "city")) missing.push("city")
  if (!strAttr(attrs, "state")) missing.push("state")
  if (!strAttr(attrs, "zip_code")) missing.push("zip_code")
  if (!strAttr(attrs, "primary_insurance_number")) missing.push("primary_insurance_number")
  if (!strAttr(attrs, "primary_insurance_name")) missing.push("primary_insurance_name")
  if (!strAttr(attrs, "primary_insurance_group_num")) missing.push("primary_insurance_group_num")
  if (!strAttr(attrs, "provider_email")) missing.push("provider_email")
  return missing
}

/** @param {Record<string, unknown>} attrs @param {Record<string, unknown>[]} batch */
function batchMultiErrors(attrs, batch) {
  const errs = []
  const pid = strAttr(attrs, "pid")
  if (!pid) return errs

  const street = strAttr(attrs, "street")
  const zipCode = strAttr(attrs, "zip_code")
  if (street && zipCode) {
    const n = batch.filter(
      (r) => strAttr(r, "street") === street && strAttr(r, "zip_code") === zipCode
    ).length
    if (n > 4) errs.push("multi_address")
  }

  const phone = strAttr(attrs, "primary_phone")
  if (phone) {
    const pn = batch.filter((r) => strAttr(r, "primary_phone") === phone).length
    if (pn > 1) errs.push("multi_phone")
  }
  return errs
}

function medAdvInsurance(primaryInsuranceName, isLegacyMbi, isActiveMbi, nameLowerTrimmed) {
  if (!primaryInsuranceName || String(primaryInsuranceName).trim() === "") return false
  if (isLegacyMbi || isActiveMbi) return false
  if (nameLowerTrimmed === "medicare") return false
  const name = String(primaryInsuranceName).toLowerCase()
  return MEDADV_MATCHERS.some((m) => name.includes(m))
}

/**
 * @param {Record<string, unknown>} attrs string-keyed row (wizard / server shape)
 * @param {Record<string, unknown>[]} batch all rows in this preview
 * @param {DxMapRow[]} mappingRows from DxCodeDeviceMapper.client_json_for_uploader
 */
export function computeUploaderCohortPreview(attrs, batch, mappingRows) {
  const insRaw = strAttr(attrs, "primary_insurance_number")
  const insNorm = insRaw.replace(/[^a-zA-Z0-9]+/g, "")
  const isLegacyMbi = legacyMbi(insNorm)
  const isActiveMbi = activeMbi(insNorm)

  const dob = parseIsoDob(attrs.dob != null ? String(attrs.dob) : "")
  const age = ageInYears(dob)
  const isSixtyFivePlus = age != null && age >= 65

  const dxList = coerceDxCodes(attrs.dx_codes)
  const dxMap = dxDeviceMapForCodes(dxList, mappingRows)
  let devices = dxMap.devices
  const qualifiedDxCodes = dxMap.qualifiedDxCodes
  const dxRequiresApproval = dxMap.dxRequiresApproval
  const isDxMatch = qualifiedDxCodes.length > 0

  if (!devices.length && attrs.devices != null) {
    const raw = Array.isArray(attrs.devices) ? attrs.devices : [attrs.devices]
    devices = [...new Set(raw.map((x) => String(x).trim()).filter(Boolean))]
  }

  const pin = strAttr(attrs, "primary_insurance_name").toLowerCase()
  const isMedAdv = medAdvInsurance(
    attrs.primary_insurance_name != null ? String(attrs.primary_insurance_name) : "",
    isLegacyMbi,
    isActiveMbi,
    pin
  )

  const isNoInsuranceMatch = !isActiveMbi && !isMedAdv
  const missing = buildMissingDemographics(attrs)

  const enrollableMedicare = isActiveMbi && isDxMatch
  const verifiableMedadv = isDxMatch && isMedAdv
  const noDxOrInsurance = !isDxMatch && isNoInsuranceMatch
  const hasDxNoInsurance = isDxMatch && isNoInsuranceMatch && !noDxOrInsurance
  const noDxHasInsurance = !isDxMatch && !isNoInsuranceMatch && !noDxOrInsurance

  const errors = []
  if (age != null && age < 0) errors.push("invalid_dob")
  const g = strAttr(attrs, "gender")
  if (g && !/^[fFmM]+$/.test(g)) errors.push("invalid_gender")
  if (pin === "medicare" && insRaw && !isActiveMbi && !isLegacyMbi && !isMedAdv) errors.push("invalid_mbi")
  errors.push(...batchMultiErrors(attrs, batch))

  return {
    devices,
    is_legacy_mbi: isLegacyMbi,
    is_active_mbi: isActiveMbi,
    is_dx_match: isDxMatch,
    is_med_adv: isMedAdv,
    is_no_insurance_match: isNoInsuranceMatch,
    is_sixty_five_plus: isSixtyFivePlus,
    dx_requires_approval: dxRequiresApproval,
    missing_demographics: missing,
    data_errors: errors,
    enrollable_medicare: enrollableMedicare,
    verifiable_medadv: verifiableMedadv,
    no_dx_or_insurance: noDxOrInsurance,
    has_dx_no_insurance: hasDxNoInsurance,
    no_dx_has_insurance: noDxHasInsurance,
    has_prev_dx_codes: false,
    has_latest_dx_codes: false,
    qualified_dx_codes: qualifiedDxCodes,
    age: dob && age != null ? age : null
  }
}

/**
 * @param {string} h
 * @param {Record<string, unknown>} serverRow
 * @param {ReturnType<typeof computeUploaderCohortPreview>} cohort
 * @param {string} signatureCsv human-readable signature methods for this row
 */
export function previewCsvCell(h, serverRow, cohort, signatureCsv) {
  if (h === "pid") {
    const p = serverRow.pid
    if (p == null) return ""
    return String(p).trim()
  }
  if (h === "patient_ehr_id") {
    const v = serverRow.patient_ehr_id
    if (v == null) return ""
    return String(v).trim()
  }
  if (h === "dx_codes") return exportDxCodesStringWithoutDots(serverRow.dx_codes)
  if (h === "signature_method") return signatureCsv
  if (h === "devices") return (cohort.devices || []).join(", ")
  if (h === "qualified_dx_codes") return exportDxCodesStringWithoutDots(cohort.qualified_dx_codes)
  if (h === "missing_demographics") return (cohort.missing_demographics || []).join(", ")
  if (h === "errors") return (cohort.data_errors || []).join(", ")
  if (h === "age") return cohort.age != null && cohort.age !== "" ? String(cohort.age) : ""
  if (COHORT_BOOL_HEADERS.has(h)) {
    const v = cohort[h]
    return v === true ? "true" : "false"
  }
  if (Object.prototype.hasOwnProperty.call(serverRow, h)) {
    const v = serverRow[h]
    if (v == null) return ""
    if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean).join(", ")
    return String(v)
  }
  return ""
}

/**
 * @param {unknown} raw json from #uploader-dx-device-map-json
 * @returns {DxMapRow[]}
 */
export function parseDxDeviceMapJson(raw) {
  if (!Array.isArray(raw)) return []
  return raw.map((r) => ({
    prefix: String(r.prefix || "").trim(),
    device: String(r.device || "").trim(),
    chronic_care: r.chronic_care === true || r.chronic_care === "true"
  }))
}
